import { useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { runGated } from "@/lib/adsGate";
import { runAiJob } from "@/lib/aiJobs";
import { toast } from "sonner";
import { Sparkles, Loader2, RefreshCw, Camera, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

/**
 * StylePreviewButton — kicks off /beauty/preview with the given kind
 * ('hair' | 'beard' | 'makeup'), style name and notes. Shows the generated
 * image in a modal so users can compare before/after side by side.
 *
 * Requires the user to have an identity photo on file — the backend will
 * return a clear 400 with a helpful message if not, which we surface as a toast.
 */
export default function StylePreviewButton({
  kind,
  styleName,
  styleNotes,
  testId,
  label,
}) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // { url, style_name }

  const run = async () => {
    setBusy(true);
    try {
      // Wrap in runGated so free-tier users who exhausted their quota are
      // shown the rewarded-ad flow instead of a dead-end toast. Quota
      // bucket = same one the backend gates on (makeup_gen for makeup,
      // tryons for hair/beard).
      //
      // /beauty/preview returns 202 { job_id } now — runAiJob unwraps it
      // by polling GET /api/ai-jobs/{job_id} until the job resolves.
      const gateKey = kind === "makeup" ? "makeup_gen" : "tryons";
      const result = await runGated(gateKey, () =>
        runAiJob(() =>
          api.post("/beauty/preview", {
            kind,
            style_name: styleName,
            style_notes: styleNotes || "",
          }),
        ),
      );
      if (!result) {
        // The user closed the ad modal (or upgrade prompt) — nothing more
        // to do; runGated already showed the right toast.
        return;
      }
      setPreview({ url: fileUrl(result.image_path), styleName });
    } catch (e) {
      // Backend sometimes returns `detail` as a dict (402 needs_upgrade payloads).
      // Naive String() → "[object Object]". Serialise properly.
      const raw = e?.response?.data?.detail;
      let msg = "Preview failed — try again.";
      if (typeof raw === "string") {
        msg = raw;
      } else if (raw && typeof raw === "object") {
        msg = raw.message || raw.error || JSON.stringify(raw);
      } else if (e?.message) {
        msg = e.message;
      }
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const defaultLabel =
    kind === "makeup" ? "Preview look on me" :
    kind === "beard" ? "Preview on me" :
    "See on me";

  return (
    <>
      <button
        type="button"
        onClick={run}
        disabled={busy}
        data-testid={testId || `preview-${kind}-btn`}
        className="inline-flex items-center gap-2 h-9 px-3 border border-accent text-accent uppercase tracking-[0.18em] text-[10px] hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-60"
      >
        {busy ? (
          <><Loader2 size={12} className="animate-spin" /> Composing</>
        ) : (
          <><Sparkles size={12} /> {label || defaultLabel}</>
        )}
      </button>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-md rounded-none p-0 overflow-hidden" data-testid="style-preview-dialog">
          <DialogHeader className="px-5 pt-5">
            <DialogTitle className="font-serif text-xl tracking-tight flex items-center gap-2">
              <Camera size={16} /> {styleName}
            </DialogTitle>
          </DialogHeader>
          {preview && (
            <>
              <img
                src={preview.url}
                alt={styleName}
                className="w-full aspect-square object-cover"
                data-testid="style-preview-image"
              />
              <div className="p-4 flex justify-between items-center border-t border-border">
                <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  AI generated · <span className="capitalize">{kind}</span>
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={run}
                    disabled={busy}
                    data-testid="style-preview-retry"
                    className="inline-flex items-center gap-1.5 h-9 px-3 border border-border text-[10px] uppercase tracking-[0.18em] hover:border-foreground"
                  >
                    <RefreshCw size={12} /> Retry
                  </button>
                  <button
                    onClick={() => setPreview(null)}
                    data-testid="style-preview-close"
                    className="inline-flex items-center gap-1.5 h-9 px-3 bg-accent text-accent-foreground text-[10px] uppercase tracking-[0.18em] hover:bg-accent/90"
                  >
                    <X size={12} /> Close
                  </button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
